"use client";

import { useErrorLogger } from "@/lib/error-logging/use-error-logger";
import { useSession } from "next-auth/react";
import { useEffect } from "react";

export function GlobalErrorHandler() {
    const { data: session } = useSession();
    const { logError, logNetworkError } = useErrorLogger();

    useEffect(() => {
        // Only set up global error handling for authenticated users
        if (!session) return;

        // Handle unhandled JavaScript errors
        const handleGlobalError = (event: ErrorEvent) => {
            const error = new Error(event.message);
            error.stack = `${event.filename}:${event.lineno}:${event.colno}\n${event.error?.stack || ""}`;

            logError(error, "javascript_error", "medium", undefined, {
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno
            });
        };

        // Handle unhandled promise rejections
        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            let error: Error;

            if (event.reason instanceof Error) {
                error = event.reason;
            } else if (typeof event.reason === "string") {
                error = new Error(event.reason);
            } else {
                error = new Error("Unhandled promise rejection: " + String(event.reason));
            }

            // Determine if this is a network error
            const isNetworkError =
                error.message.includes("fetch") ||
                error.message.includes("NetworkError") ||
                error.message.includes("Failed to fetch");

            if (isNetworkError) {
                logNetworkError(error, window.location.href, "unknown");
            } else {
                logError(error, "javascript_error", "medium", undefined, {
                    promiseRejection: true,
                    reason: String(event.reason)
                });
            }
        };

        const originalFetch = window.fetch;
        window.fetch = async (...args) => {
            try {
                const response = await originalFetch(...args);

                if (!response.ok && response.status >= 500) {
                    const url = args[0]?.toString() || "unknown";
                    const method = args[1]?.method || "GET";

                    logNetworkError(
                        new Error(`HTTP ${response.status}: ${response.statusText}`),
                        url,
                        method,
                        response.status
                    );
                }

                return response;
            } catch (error) {
                const url = args[0]?.toString() || "unknown";
                const method = args[1]?.method || "GET";

                if (error instanceof Error) {
                    logNetworkError(error, url, method);
                }

                throw error;
            }
        };

        window.addEventListener("error", handleGlobalError);
        window.addEventListener("unhandledrejection", handleUnhandledRejection);

        return () => {
            window.removeEventListener("error", handleGlobalError);
            window.removeEventListener("unhandledrejection", handleUnhandledRejection);
            window.fetch = originalFetch;
        };
    }, [session, logError, logNetworkError]);

    return null;
}
